/**
 * Design tokens — single source of truth.
 *
 * Consumed by BOTH:
 *  - tailwind.config.js  (via require())
 *  - src/styles/theme.ts (MUI createTheme, via default import)
 *
 * Values are either the existing hardcoded colors (so nothing shifts
 * unexpectedly) or tints/shades derived from the brand #0045FF.
 * No new palette is invented — this formalizes the existing one into scales.
 *
 * NOTE: This file is CommonJS (.cjs) on purpose so that tailwind.config.js
 * can `require()` it at Node config-eval time under CRA's built-in PostCSS.
 */

const colors = {
  primary: {
    50: "#EBF0FF",
    100: "#D6E0FF",
    200: "#ADC2FF",
    300: "#85A3FF",
    400: "#3D6BFF",
    500: "#0045FF", // BRAND — unchanged
    600: "#0038D6",
    700: "#0031BA", // == existing button-hover
    800: "#00258C",
    900: "#001A66",
  },
  neutral: {
    0: "#FFFFFF",
    50: "#F7F8F9", // == existing background
    100: "#EFF1F3",
    200: "#EAEAED", // == existing borderGray
    300: "#D7D9DE",
    400: "#A0A3BD", // == existing StyledTab inactive
    500: "#818181", // == existing table-header
    600: "#575F6E", // == existing text-annotation
    700: "#3D4351",
    800: "#2D2D2D", // == existing text-main
    900: "#1A1A1A",
  },
  success: { light: "#E6F6EC", main: "#1E9E54", dark: "#157A40" },
  warning: { light: "#FEF3C7", main: "#D97706", dark: "#92610A" },
  error: { light: "#FDECEC", main: "#D32F2F", dark: "#B71C1C" },
  info: { light: "#E7F0FF", main: "#0045FF", dark: "#0031BA" },
};

const semantic = {
  surface: colors.neutral[0],
  background: colors.neutral[50],
  border: colors.neutral[200],
  textMain: colors.neutral[800],
  textMuted: colors.neutral[600],
  tableHeader: colors.neutral[500],
};

const radius = {
  sm: "6px",
  DEFAULT: "8px",
  md: "10px",
  lg: "12px",
  xl: "16px",
  large: "2.5rem", // preserve existing borderRadius.large
};

const shadow = {
  xs: "0 1px 2px 0 rgba(16,24,40,0.05)",
  sm: "0 1px 3px 0 rgba(16,24,40,0.08), 0 1px 2px -1px rgba(16,24,40,0.06)",
  md: "0 4px 8px -2px rgba(16,24,40,0.10), 0 2px 4px -2px rgba(16,24,40,0.06)",
  lg: "0 12px 16px -4px rgba(16,24,40,0.08), 0 4px 6px -2px rgba(16,24,40,0.03)",
};

const fontFamily = {
  en: ['"DM Sans"', '"Segoe UI"', "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"],
  ja: [
    '"Noto Sans JP"',
    '"M PLUS Rounded 1c"',
    '"Kosugi Maru"',
    '"Hiragino Maru Gothic ProN"',
    '"BIZ UDPGothic"',
    "Meiryo",
    "sans-serif",
  ],
};

module.exports = { colors, semantic, radius, shadow, fontFamily };
